import { useState } from "react";
import {
  UserPlus, Stethoscope, Pill, FlaskConical, CalendarDays, CheckSquare,
  CreditCard, MessageSquare, RefreshCw, Sparkles, HeartPulse,
} from "lucide-react";

const CONFIG = {
  joined: { icon: UserPlus, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  consultation: { icon: Stethoscope, color: "text-sky-600 bg-sky-50 border-sky-200" },
  treatment_plan: { icon: Pill, color: "text-violet-600 bg-violet-50 border-violet-200" },
  lab: { icon: FlaskConical, color: "text-amber-600 bg-amber-50 border-amber-200" },
  appointment: { icon: CalendarDays, color: "text-sky-600 bg-sky-50 border-sky-200" },
  followup: { icon: CheckSquare, color: "text-slate-600 bg-slate-50 border-slate-200" },
  payment: { icon: CreditCard, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  renewal: { icon: RefreshCw, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  message: { icon: MessageSquare, color: "text-slate-600 bg-slate-50 border-slate-200" },
  checkin: { icon: HeartPulse, color: "text-rose-600 bg-rose-50 border-rose-200" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "treatment_plan", label: "Treatment" },
  { key: "lab", label: "Labs" },
  { key: "message", label: "Messages" },
  { key: "checkin", label: "Check-ins" },
  { key: "payment", label: "Payments" },
];

function fmt(date) {
  try {
    return new Date(date).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return date;
  }
}

export default function Timeline({ events, testid }) {
  const [filter, setFilter] = useState("all");
  if (!events || events.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No timeline activity yet.</p>;
  }
  const matches = (e) => filter === "all" || e.type === filter || (filter === "payment" && e.type === "renewal");
  const shown = events.filter(matches);

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="timeline-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`timeline-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Nothing in this category yet.</p>
      ) : (
        <ol className="relative" data-testid={testid}>
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />
          {shown.map((e, i) => {
            const cfg = CONFIG[e.type] || { icon: Sparkles, color: "text-slate-600 bg-slate-50 border-slate-200" };
            const Icon = cfg.icon;
            return (
              <li key={i} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`timeline-event-${i}`}>
                <div className={`relative z-10 w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${cfg.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 pt-1">
                  <p className="text-sm font-medium leading-snug">{e.title}</p>
                  {e.detail && <p className="text-sm text-muted-foreground mt-0.5">{e.detail}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{fmt(e.date)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
